import {
  HoverCard,
  Popover,
  Portal,
  PortalProps,
  UseDisclosureReturn,
  useDisclosure,
  useMediaQuery
} from "@chakra-ui/react"
import { MouseEvent } from "react"
import useHoverCardOrPopover from "../chakra/useHoverCardOrPopover"

type TooltipRootProps = Partial<
  Popover.RootProps & Pick<HoverCard.RootProps, "openDelay" | "closeDelay">
>

export type TooltipProps = {
  children: React.ReactNode
  title?: string | React.ReactNode
  fitContent?: boolean
  popover?: TooltipRootProps
  content?: Partial<Popover.ContentProps>
  portal?: Partial<PortalProps> | boolean
  disclosure?: UseDisclosureReturn
  trigger?: "hover" | "click"
}

function Tooltip({
  children,
  title,
  fitContent,
  popover,
  content,
  disclosure: disclosureProp,
  portal = true,
  trigger = "hover"
}: TooltipProps) {
  // Optionally force popover to fit width of content.
  // Otherwise, popover will render content at a max-width.
  const contentProps = fitContent
    ? {
        ...content,
        width: "auto",
        whiteSpace: "nowrap"
      }
    : content

  const disclosure = useDisclosure()
  // Control the popover to allow for closing the popover when clicking
  // the trigger.
  const { open, onClose, onOpen } = disclosureProp || disclosure

  // Hover tooltips stay off touch devices, where an incidental tap should not
  // introduce a popover. Explicit click-triggered help remains available.
  const [hasHover] = useMediaQuery(["(hover: hover)"])

  const portalProps = portal === true ? {} : portal

  const HoverCardOrPopover = useHoverCardOrPopover({
    ignoreHover: trigger === "click"
  })

  return title && (hasHover || trigger === "click") ? (
    <HoverCardOrPopover.Root
      open={open}
      onOpenChange={(e: { open: boolean }) => (e.open ? onOpen() : onClose())}
      closeOnInteractOutside={HoverCardOrPopover === Popover}
      closeOnEscape
      portalled={!!portal}
      {...popover}
    >
      <HoverCardOrPopover.Trigger asChild>
        {children}
      </HoverCardOrPopover.Trigger>
      <Portal disabled={!portal} {...portalProps}>
        <HoverCardOrPopover.Positioner>
          <HoverCardOrPopover.Content
            className="TooltipContent"
            fontSize="sm"
            color="whiteAlpha.900"
            p={2}
            borderRadius="md"
            boxShadow="lg"
            onClick={(e: MouseEvent<HTMLDivElement>) => {
              e.stopPropagation()
            }}
            css={{
              "--popover-bg": "black",
              "--hovercard-bg": "black"
            }}
            {...contentProps}
          >
            <HoverCardOrPopover.Arrow>
              <HoverCardOrPopover.ArrowTip
                borderWidth="0"
                borderColor="transparent"
              />
            </HoverCardOrPopover.Arrow>
            {title}
          </HoverCardOrPopover.Content>
        </HoverCardOrPopover.Positioner>
      </Portal>
    </HoverCardOrPopover.Root>
  ) : (
    children
  )
}

export default Tooltip
